import type { Plugin } from 'vite'
import {
  scanForBlockedStorage,
  formatStorageViolationError,
  type StorageLinterOptions,
} from './storageLinter'

export type StorageCheckSeverity = 'error' | 'warn' | 'off'

export interface StorageCheckPluginOptions extends StorageLinterOptions {
  /**
   * Whether the storage linter is enabled.
   * Defaults to true unless RUNDOT_DISABLE_STORAGE_CHECK=true.
   */
  enabled?: boolean

  /**
   * Severity level: 'error' (default, fails build/dev) or 'warn' (logs warning) or 'off'.
   */
  severity?: StorageCheckSeverity

  /**
   * Custom include filter (e.g. file extensions or path regex).
   */
  include?: RegExp

  /**
   * Custom exclude filter.
   */
  exclude?: RegExp
}

const DEFAULT_INCLUDE = /\.(?:[jt]sx?|m[jt]s|c[jt]s|vue|svelte)$/i
const DEFAULT_EXCLUDE = /(?:[\\/]node_modules[\\/]|[\\/]packages[\\/]sdk[\\/])/

export function isStorageCheckDisabled(): boolean {
  return (
    process.env.RUNDOT_DISABLE_STORAGE_CHECK === 'true' ||
    process.env.RUNDOT_DISABLE_STORAGE_CHECK === '1' ||
    process.env.RUNDOT_STORAGE_CHECK === 'false' ||
    process.env.RUNDOT_STORAGE_CHECK === 'off'
  )
}

export function getResolvedStorageCheckSeverity(
  configuredSeverity?: StorageCheckSeverity,
): StorageCheckSeverity {
  if (isStorageCheckDisabled()) return 'off'
  if (process.env.RUNDOT_STORAGE_CHECK === 'warn') return 'warn'
  if (process.env.RUNDOT_STORAGE_CHECK === 'error') return 'error'
  return configuredSeverity || 'error'
}

export function rundotStorageCheckPlugin(
  options: StorageCheckPluginOptions = {},
): Plugin {
  const isExplicitlyDisabled = options.enabled === false
  const severity = isExplicitlyDisabled ? 'off' : getResolvedStorageCheckSeverity(options.severity)
  const include = options.include || DEFAULT_INCLUDE
  const exclude = options.exclude || DEFAULT_EXCLUDE

  return {
    name: 'vite-plugin-rundot-storage-check',
    enforce: 'pre',

    transform(code: string, id: string) {
      if (severity === 'off') return null
      if (id.startsWith('\0') || id.startsWith('virtual:')) return null
      if (exclude.test(id) || !include.test(id)) return null

      const violations = scanForBlockedStorage(code, id, options)
      if (violations.length === 0) return null

      const formattedMessages = violations.map((v) =>
        formatStorageViolationError(v, code, id),
      )
      const combinedMessage = formattedMessages.join('\n\n')

      if (severity === 'warn') {
        console.warn(`\n${combinedMessage}\n`)
        return null
      }

      // Default: 'error'
      throw new Error(`\n${combinedMessage}\n`)
    },
  }
}
